import { VirtualList } from '@epam/promo';
import {
  DndActor,
  DndActorRenderParams,
  DropParams,
  getOrderBetween,
} from '@epam/uui-core';
import { sortBy } from 'lodash';
import { useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { AllStatuses } from 'interfaces';
import {
  useModal,
  useTaskDeleteQuery,
  useTaskReorderQuery,
  useTaskSwitcher,
} from 'hooks';
import { useUserRole } from 'hooks/helpers';
import { validateHTML } from 'hooks/helpers/common';
import { Task } from 'models/task.model';
import { taskRoutes } from 'router/taskRoutes';
import { resetDraft, setDraftTask, useTaskStore } from 'store/task';
import { Card } from '../Card';
import css from './CardList.module.scss';

interface CardListProps {
  items: Task[];
  isSearchActive?: boolean;
}

export const CardList = ({
  items: tasks,
  isSearchActive = false,
}: CardListProps) => {
  const { isSuperAdmin } = useUserRole();
  const sortedTasks = useMemo(() => sortBy(tasks, ['order']), [tasks]);
  const navigate = useNavigate();
  const location = useLocation();
  const switchTask = useTaskSwitcher();
  const modal = useModal();
  const draftTask = useTaskStore((state) => state.draft);
  const { mutate: deleteTask, discardDraft } = useTaskDeleteQuery();
  const { mutate: reorderTask } = useTaskReorderQuery();

  const canAcceptDrop = () => ({
    top: true,
    bottom: true,
  });

  const handleOnDrop = (params: DropParams<Task, Task>) => {
    const { srcData, dstData, position } = params;

    const current = tasks.findIndex(({ id }) => id === dstData?.id);

    const newOrder =
      position === 'bottom'
        ? getOrderBetween(dstData?.order ?? null, tasks[current + 1]?.order)
        : getOrderBetween(tasks[current - 1]?.order, dstData?.order ?? null);

    const isNotDraggedGroup = dstData?.id !== srcData.id;
    const isSameGroup = dstData?.taskGroupId === srcData.taskGroupId;

    if (isNotDraggedGroup && isSameGroup) {
      reorderTask({
        id: srcData.id,
        order: newOrder,
      });
    }
  };

  const renderMaterial = (currentItem: Task) => (
    <DndActor
      key={currentItem.id}
      canAcceptDrop={canAcceptDrop}
      dstData={currentItem}
      render={(params: DndActorRenderParams) => (
        <Card
          key={currentItem.id}
          isDragable
          dndParams={params}
          isActive={
            currentItem.id === draftTask.id &&
            location.pathname !== taskRoutes.notFound
          }
          item={currentItem}
          onClick={() => {
            if (
              currentItem.id !== draftTask.id ||
              location.pathname === taskRoutes.notFound
            )
              switchTask({
                onSuccess: () => {
                  resetDraft();
                  setDraftTask({
                    task: currentItem,
                  });
                  navigate(taskRoutes.taskId(currentItem.id));
                },
                onAbort: () => navigate(taskRoutes.taskId(currentItem.id)),
              });
          }}
          onDelete={() =>
            currentItem.id
              ? modal.delete({
                  text: `Are you sure you want to Delete ${
                    currentItem.title
                      ? `"${validateHTML(currentItem.title)}"`
                      : 'this task'
                  }?`,
                  highlights: [
                    `${
                      currentItem.title
                        ? `"${validateHTML(currentItem.title)}"`
                        : 'this task'
                    }`,
                    'Delete',
                  ],
                  onSuccess: () => deleteTask(currentItem.id),
                })
              : modal.delete({
                  text: 'Are you sure you want to Delete this draft?',
                  highlights: ['Delete', 'draft'],
                  onSuccess: () => discardDraft(),
                })
          }
        />
      )}
      srcData={currentItem}
      onDrop={(params) => handleOnDrop(params)}
    />
  );

  return (
    <VirtualList
      cx={css.list}
      role="listbox"
      rows={
        isSuperAdmin &&
        draftTask.status !== AllStatuses.NOT_SAVED &&
        !isSearchActive
          ? sortedTasks.map((task) => renderMaterial(task))
          : tasks.map((item) => (
              <Card
                key={item.id}
                isActive={
                  item.id === draftTask.id &&
                  location.pathname !== taskRoutes.notFound
                }
                item={item}
                onClick={() => {
                  if (
                    item.id !== draftTask.id ||
                    location.pathname === taskRoutes.notFound
                  )
                    switchTask({
                      onSuccess: () => {
                        resetDraft();
                        setDraftTask({
                          task: item,
                        });
                        navigate(taskRoutes.taskId(item.id));
                      },
                      onAbort: () => navigate(taskRoutes.taskId(item.id)),
                    });
                }}
                onDelete={() =>
                  item.id
                    ? modal.delete({
                        text: `Are you sure you want to Delete ${
                          item.title ? `"${item.title}"` : 'this task'
                        }?`,
                        highlights: [
                          `${item.title ? `"${item.title}"` : 'this task'}`,
                          'Delete',
                        ],
                        onSuccess: () => deleteTask(item.id),
                      })
                    : modal.delete({
                        text: 'Are you sure you want to Delete this draft?',
                        highlights: ['Delete', 'draft'],
                        onSuccess: () => discardDraft(),
                      })
                }
              />
            )) || []
      }
      rowsCount={tasks.length}
      value={{ topIndex: 0 }}
      onValueChange={() => {}}
    />
  );
};
